import React, { useState, useEffect, useCallback } from 'react';
import { Button, Tag, Space, Typography, Empty, Spin, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faEnvelopeOpenText,
  faReply,
  faClockRotateLeft,
  faCommentSms,
  faChevronDown,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import caseService from '../../services/CaseService';
import ComposeOutreachModal from '../../pages/CaseManagement/components/ComposeOutreachModal';
import { COLORS } from '../../constants/colors';

const { Text, Title, Paragraph } = Typography;

const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '');
const formatDateTime = (d) => (d ? new Date(d).toLocaleString() : '');

/**
 * PatientEmailPanel — the "Email" tab on member records / intake edit.
 * Lets staff email the member and shows outreach status, their reply, and an
 * activity timeline. Reuses the outreach compose modal + endpoints so activity
 * is consistent with the Outreach page.
 */
const PatientEmailPanel = ({ patientId, name, email, phone }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [showSent, setShowSent] = useState(false);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const res = await caseService.getMemberOutreach(patientId);
      setData(res.data || null);
    } catch (e) {
      message.error('Failed to load email activity');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const status = data?.status || {};
  const resolvedEmail = data?.email || email || null;
  const resolvedName = data?.name || name || '';
  const resolvedPhone = data?.phone || phone || null;

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 16
        }}
      >
        <div>
          <Title level={5} style={{ margin: 0 }}>
            <FontAwesomeIcon icon={faEnvelope} style={{ color: COLORS.$primaryBlue, marginRight: 8 }} />
            Email Outreach
          </Title>
          <Text type="secondary">{resolvedEmail || 'No email on file'}</Text>
        </div>
        <Button
          type="primary"
          icon={<FontAwesomeIcon icon={faEnvelope} />}
          disabled={!resolvedEmail}
          onClick={() => setComposeOpen(true)}
        >
          Compose Email
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin indicator={<LoadingOutlined spin style={{ fontSize: 28, color: '#1a1a1a' }} />} />
        </div>
      ) : (
        <>
          <Space size={[8, 8]} wrap style={{ marginBottom: 16 }}>
            {status.emailSent ? (
              <Tag
                color="blue"
                icon={<FontAwesomeIcon icon={status.emailOpened ? faEnvelopeOpenText : faEnvelope} />}
              >
                {status.emailOpened ? 'Email opened' : 'Emailed'}
                {status.emailSentDate ? ` · ${formatDate(status.emailSentDate)}` : ''}
              </Tag>
            ) : (
              <Tag>Not emailed yet</Tag>
            )}
            {status.emailReplied && (
              <Tag color="green" icon={<FontAwesomeIcon icon={faReply} />}>Replied by email</Tag>
            )}
            {status.textSent && (
              <Tag color="geekblue" icon={<FontAwesomeIcon icon={faCommentSms} />}>
                Texted
                {status.textSentDate ? ` · ${formatDate(status.textSentDate)}` : ''}
              </Tag>
            )}
            {status.textReplied && (
              <Tag color="green" icon={<FontAwesomeIcon icon={faReply} />}>Replied by text</Tag>
            )}
          </Space>

          {(status.lastEmailHtml || status.lastTextBody) && (
            <div style={{ marginBottom: 16 }}>
              <Button
                type="link"
                size="small"
                style={{ paddingLeft: 0 }}
                onClick={() => setShowSent((v) => !v)}
                icon={<FontAwesomeIcon icon={showSent ? faChevronDown : faChevronRight} />}
              >
                {showSent ? 'Hide message we sent' : 'View message we sent'}
              </Button>
              {showSent && (
                <div
                  style={{
                    background: '#fafafa',
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    padding: '12px 16px',
                    marginTop: 8
                  }}
                >
                  {status.lastEmailHtml && (
                    <>
                      {status.lastEmailSubject && (
                        <Paragraph style={{ marginBottom: 8 }}>
                          <Text type="secondary">Subject: </Text>
                          <Text strong>{status.lastEmailSubject}</Text>
                        </Paragraph>
                      )}
                      <div
                        style={{ maxHeight: 320, overflow: 'auto', fontSize: 14, lineHeight: 1.6 }}
                        // Content was sanitized server-side before sending.
                        dangerouslySetInnerHTML={{ __html: status.lastEmailHtml }}
                      />
                    </>
                  )}
                  {status.lastTextBody && (
                    <div style={{ marginTop: status.lastEmailHtml ? 12 : 0 }}>
                      <Paragraph style={{ marginBottom: 4 }}>
                        <Text type="secondary">
                          <FontAwesomeIcon icon={faCommentSms} style={{ marginRight: 6 }} />
                          Text message sent:
                        </Text>
                      </Paragraph>
                      <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                        {status.lastTextBody}
                      </Paragraph>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {status.emailReplyText && (
            <div
              style={{
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: 8,
                padding: '12px 16px',
                marginBottom: 16
              }}
            >
              <Text strong>
                <FontAwesomeIcon icon={faReply} style={{ marginRight: 6 }} />
                Their email reply
                {status.emailReplyDate ? ` · ${formatDateTime(status.emailReplyDate)}` : ''}
              </Text>
              <Paragraph style={{ marginTop: 8, marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                {status.emailReplyText}
              </Paragraph>
            </div>
          )}

          {status.textReplyText && (
            <div
              style={{
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: 8,
                padding: '12px 16px',
                marginBottom: 16
              }}
            >
              <Text strong>
                <FontAwesomeIcon icon={faCommentSms} style={{ marginRight: 6 }} />
                Their text reply
                {status.textReplyDate ? ` · ${formatDateTime(status.textReplyDate)}` : ''}
              </Text>
              <Paragraph style={{ marginTop: 8, marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                {status.textReplyText}
              </Paragraph>
            </div>
          )}

          <Title level={5} style={{ marginTop: 8 }}>
            <FontAwesomeIcon icon={faClockRotateLeft} style={{ color: COLORS.textMedium, marginRight: 8 }} />
            Activity
          </Title>
          {!data?.activity || data.activity.length === 0 ? (
            <Empty description="No outreach activity yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <div>
              {data.activity.map((a, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 12,
                    padding: '8px 0',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                >
                  <div style={{ color: COLORS.textMuted, fontSize: 12, minWidth: 140 }}>
                    {formatDateTime(a.at)}
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.textDark }}>
                    {a.text}
                    {a.by ? <span style={{ color: COLORS.textMuted }}> — {a.by}</span> : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <ComposeOutreachModal
        visible={composeOpen}
        method="email"
        recipients={[{ patientId, name: resolvedName, email: resolvedEmail, phone: resolvedPhone }]}
        onClose={() => setComposeOpen(false)}
        onSent={() => {
          setComposeOpen(false);
          load();
        }}
      />
    </div>
  );
};

export default PatientEmailPanel;
